/**
 * ALQUIMIA CHARMS — Recomendaciones de carrito (V1.2)
 * ---------------------------------------------------------------
 * - Quick add (1 clic) de productos recomendados vía /cart/add.js
 * - Refresca carrito + recomendaciones sin recargar la página, usando la
 *   Section Rendering API nativa de Shopify (?sections=main)
 * - Dispara eventos de analítica (dataLayer + GA4/Meta/TikTok si existen)
 *
 * V1.1 (rendimiento): refreshCartSections() resuelve el elemento destino ANTES
 * de pedir nada por red. Antes pedía /cart?sections=main y solo después
 * buscaba #shopify-section-main; como Minimog genera ids con prefijo de
 * plantilla (shopify-section-template--XXXX__main), ese getElementById nunca
 * encontraba nada y la respuesta se descartaba SIEMPRE. El coste era de 2 a 3
 * descargas completas de la página /cart por cada add-to-cart, encadenadas con
 * el /cart y /cart.json propios del tema: seis peticiones de estado de carrito
 * por una sola acción. Eso saturaba la AJAX API de Shopify (429) y dejaba el
 * carrito aparentemente congelado varios segundos.
 *
 * V1.2 (badge del header): handleAdd() ignoraba por completo el body de la
 * respuesta de /cart/add.js y nunca avisaba a assets/cart.js (ni vía
 * MinimogEvents ni vía CustomEvent) de que el carrito había cambiado. Fuera
 * de /cart (p.ej. en el cart drawer, o en "Completa tu 4+2"), refreshCartSections()
 * tampoco actualizaba el header porque su target (#shopify-section-main) solo
 * existe en la página /cart. Resultado: el badge del header se quedaba
 * desactualizado tras un quick-add fuera de esa página.
 * Fix: se agrega `sections: ['header']` al MISMO POST a /cart/add.js (no es un
 * request nuevo, es el mismo request pidiendo un dato extra en su respuesta) y
 * se extrae el item_count real del HTML de esa sección para actualizar
 * .m-cart-count-bubble de inmediato, en cualquier contexto.
 *
 * Todo el módulo está escrito de forma defensiva: si algo no existe
 * (un helper de otra librería, un selector) simplemente no hace nada,
 * nunca debe romper el resto del carrito ni el checkout.
 */
(function () {
  'use strict';

  // Este script puede quedar referenciado tanto en la página de carrito como
  // en el cart drawer; si ambos llegan a cargar en el mismo documento, evita
  // registrar los listeners dos veces (lo que duplicaría cada add-to-cart).
  if (window.__alquimiaCartRecsInit) return;
  window.__alquimiaCartRecsInit = true;

  var IMPRESSED = new Set();
  var ADDED_VIA_REC = (function () {
    try {
      var raw = sessionStorage.getItem('alquimia_rec_added');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch (e) {
      return new Set();
    }
  })();

  function persistAddedSet() {
    try {
      sessionStorage.setItem('alquimia_rec_added', JSON.stringify(Array.from(ADDED_VIA_REC)));
    } catch (e) {
      /* no-op: sessionStorage puede fallar en modo privado, no es crítico */
    }
  }

  function pushEvent(name, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, payload || {}));
    } catch (e) {
      /* no-op */
    }
  }

  // Además del evento propio, replicamos los eventos estándar de GA4/Meta/TikTok
  // cuando corresponde, para que el sistema de recomendaciones alimente el mismo
  // funnel de medición que ya usa el resto de la tienda (Fase 20/21 del proyecto).
  function pushStandardAddToCart(card, variantId) {
    var price = parseFloat(card.getAttribute('data-price') || '0');
    var itemPayload = {
      item_id: card.getAttribute('data-product-id'),
      item_variant: variantId,
      price: price,
      quantity: 1,
    };
    pushEvent('add_to_cart', {
      ecommerce: {
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'MXN',
        value: price,
        items: [Object.assign({ item_list_id: 'cart_recommendation', item_list_name: 'Recomendaciones de carrito' }, itemPayload)],
      },
    });
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'AddToCart', {
          content_ids: [card.getAttribute('data-product-id')],
          content_type: 'product',
          value: price,
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'MXN',
        });
      }
      if (window.ttq && typeof window.ttq.track === 'function') {
        window.ttq.track('AddToCart', {
          content_id: card.getAttribute('data-product-id'),
          value: price,
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'MXN',
        });
      }
    } catch (e) {
      /* no-op */
    }
  }

  function cardPayload(card, extra) {
    return Object.assign(
      {
        recommended_product_id: card.getAttribute('data-product-id'),
        recommendation_type: card.getAttribute('data-reason'),
        position: card.getAttribute('data-position'),
      },
      extra || {}
    );
  }

  function initImpressionTracking(root) {
    if (!('IntersectionObserver' in window)) return;
    var cards = root.querySelectorAll('[data-alquimia-rec-card]');
    if (!cards.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var card = entry.target;
          var id = card.getAttribute('data-product-id');
          if (IMPRESSED.has(id)) return;
          IMPRESSED.add(id);
          var moduleEl = card.closest('[data-alquimia-cart-recs]');
          var context = moduleEl ? moduleEl.getAttribute('data-context') : 'page';
          pushEvent('recommendation_impression', cardPayload(card));
          pushEvent(context === 'drawer' ? 'checkout_recommendation_view' : 'cart_recommendation_view', cardPayload(card));
          observer.unobserve(card);
        });
      },
      { threshold: 0.5 }
    );

    cards.forEach(function (card) {
      observer.observe(card);
    });
  }

  function setButtonState(button, state) {
    button.classList.remove('is-loading', 'is-added', 'is-error');
    if (state) button.classList.add(state);
  }

  // Actualiza el badge del header (.m-cart-count-bubble) con el item_count
  // real, extraído del HTML de la sección "header" que YA viene incluido en
  // la respuesta de /cart/add.js (se pidió viá `sections: ['header']` en el
  // mismo POST) -- cero requests adicionales. También emite el evento del bus
  // del tema para que assets/cart.js (y cualquier otro listener) quede
  // sincronizado, por si hay mas de un badge instanciado en la página.
  function updateHeaderCartCount(addResponse) {
    try {
      if (!addResponse || !addResponse.sections || !addResponse.sections.header) return;
      var doc = new DOMParser().parseFromString(addResponse.sections.header, 'text/html');
      var freshBubble = doc.querySelector('.m-cart-count-bubble');
      if (!freshBubble) return;
      var itemCount = parseInt((freshBubble.textContent || '').trim(), 10);
      if (isNaN(itemCount)) return;

      document.querySelectorAll('.m-cart-count-bubble').forEach(function (bubble) {
        if (itemCount > 0) {
          bubble.textContent = itemCount;
          bubble.classList.remove('m:hidden');
        } else {
          bubble.classList.add('m:hidden');
        }
      });

      if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
          item_count: itemCount,
          source: 'alquimia-cart-recommendations',
        });
      }
    } catch (e) {
      /* no-op: si algo falla aqui, el badge se corrige en el siguiente cambio de cantidad */
    }
  }

  var refreshing = false;

  function refreshCartSections() {
    // Resolvemos el destino ANTES de pedir nada por red. Si no existe, la
    // respuesta se descartaría igual: pedirla solo cuesta ancho de banda y
    // cuota de la AJAX API. Ver nota de rendimiento en la cabecera.
    var target = document.getElementById('shopify-section-main');
    if (!target) return Promise.resolve();
    if (refreshing) return Promise.resolve();
    refreshing = true;

    // Section Rendering API: pide de nuevo el HTML de la sección "main" del
    // carrito (definida en templates/cart.json) — incluye items, subtotal,
    // botón de pago Y este mismo módulo de recomendaciones ya recalculado.
    var cartUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart';
    return fetch(cartUrl + '?sections=main', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('No se pudo actualizar el carrito');
        return res.json();
      })
      .then(function (data) {
        if (data && data.main) {
          target.innerHTML = data.main;
          initImpressionTracking(target);
          observeItemsContainer();
        }
      })
      .catch(function () {
        /* Si falla el refresh en vivo, el carrito sigue siendo válido:
           el próximo cambio de cantidad o un refresh manual lo corrige. */
      })
      .then(function () {
        refreshing = false;
      });
  }

  function handleAdd(button) {
    if (button.classList.contains('is-loading') || button.classList.contains('is-added')) return;
    var card = button.closest('[data-alquimia-rec-card]');
    if (!card) return;

    var variantId = button.getAttribute('data-variant-id');
    if (button.getAttribute('data-needs-variant') === 'true') {
      var select = card.querySelector('[data-alquimia-rec-variant-select]');
      if (select) variantId = select.value;
    }
    if (!variantId) return;

    setButtonState(button, 'is-loading');

    var addUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart/add.js';

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: ['header'],
        sections_url: window.location.pathname,
      }),
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      })
      .then(function (data) {
        setButtonState(button, 'is-added');
        var productId = card.getAttribute('data-product-id');
        ADDED_VIA_REC.add(productId);
        persistAddedSet();

        var reason = card.getAttribute('data-reason');
        pushEvent('recommendation_add_to_cart', cardPayload(card));
        pushEvent(reason === 'complemento' ? 'cross_sell_add' : 'upsell_add', cardPayload(card));
        pushStandardAddToCart(card, variantId);

        updateHeaderCartCount(data);
        refreshCartSections();
      })
      .catch(function () {
        setButtonState(button, 'is-error');
        window.setTimeout(function () {
          setButtonState(button, null);
        }, 2500);
      });
  }

  // Delegación de eventos en document: sobrevive a que Shopify reemplace el
  // HTML del carrito (Section Rendering API) sin necesidad de re-atar listeners.
  document.addEventListener('click', function (event) {
    var addButton = event.target.closest('[data-alquimia-rec-add]');
    if (addButton) {
      event.preventDefault();
      handleAdd(addButton);
      return;
    }

    var trackEl = event.target.closest('[data-alquimia-rec-card]');
    if (trackEl && (event.target.closest('a') || event.target === trackEl)) {
      pushEvent('recommendation_click', cardPayload(trackEl));
    }
  });

  // Si el usuario elimina del carrito un producto que había llegado por una
  // recomendación, lo marcamos para analítica (best-effort: depende de que el
  // botón nativo de "Eliminar" del tema esté dentro del mismo item del carrito).
  document.addEventListener('click', function (event) {
    var removeTrigger = event.target.closest('[data-cart-item] .m-cart-item__remove, [data-cart-item] m-cart-remove-button');
    if (!removeTrigger) return;
    var itemEl = event.target.closest('[data-cart-item]');
    if (!itemEl) return;
    var productId = itemEl.getAttribute('data-product-id');
    if (productId && ADDED_VIA_REC.has(productId)) {
      pushEvent('recommendation_remove', { recommended_product_id: productId });
      ADDED_VIA_REC.delete(productId);
      persistAddedSet();
    }
  });

  function initAll() {
    document.querySelectorAll('[data-alquimia-cart-recs]').forEach(function (root) {
      initImpressionTracking(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Si el tema (Minimog) vuelve a pintar el contenedor de items del carrito
  // por su cuenta (cambio de cantidad, eliminar producto), nuestro módulo de
  // recomendaciones vive fuera de ese contenedor, así que lo resincronizamos.
  var currentObserver = null;
  var observerDebounce = null;
  function observeItemsContainer() {
    if (!('MutationObserver' in window)) return;
    if (currentObserver) currentObserver.disconnect();
    var itemsContainer = document.querySelector('[data-minimog-cart-items]');
    if (!itemsContainer) return;
    currentObserver = new MutationObserver(function () {
      window.clearTimeout(observerDebounce);
      observerDebounce = window.setTimeout(refreshCartSections, 400);
    });
    currentObserver.observe(itemsContainer, { childList: true, subtree: true });
  }
  observeItemsContainer();
})();
